import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animated, useSpring, config } from '@react-spring/web';
import { createPortal } from 'react-dom';
import Flyout from '@pushpay/flyout';
import { createUseStyles } from '@pushpay/styles';
import { useForwardedRef, useOnClickOutside } from '@pushpay/utils';
import { useNavigation } from './use-navigation';
const flyoutPaddingV = 4;
const useStyles = createUseStyles(theme => ({
  root: {
    position: 'absolute'
  },
  flyout: {
    padding: `${flyoutPaddingV}px 0`,
    minWidth: '180px',
    marginLeft: theme.SPACING.XSMALL
  }
}));
export const NavigationFlyout = /*#__PURE__*/forwardRef(function NavigationFlyout({
  anchorElement,
  toggleFlyout,
  children,
  classes: classesProp
}, ref) {
  const classes = useStyles(classesProp);
  const {
    navRef,
    onNavKeyDown,
    navContainerRef
  } = useNavigation();
  const [bindFlyoutRef, flyoutRef] = useForwardedRef(ref);
  const navContainerElement = navContainerRef.current;
  const getAnchorPositionTop = useCallback(() => {
    if (!anchorElement.current) {
      return 0;
    }

    const anchorRect = anchorElement.current.getBoundingClientRect();
    const anchorTop = anchorRect.top - flyoutPaddingV;
    const sidebarRect = navContainerElement.getBoundingClientRect();

    if (anchorTop < sidebarRect.top) {
      return sidebarRect.top + window.scrollY;
    }

    const flyoutHeight = flyoutRef.current.clientHeight;

    if (anchorTop + flyoutHeight > sidebarRect.bottom) {
      return sidebarRect.bottom - flyoutHeight + window.scrollY;
    }

    return anchorTop + window.scrollY;
  }, [anchorElement, flyoutRef, navContainerElement]);
  const [style, animatePositionApi] = useSpring(() => {
    var _anchorElement$curren, _anchorElement$curren2, _anchorElement$curren3;

    return {
      top: window.scrollY + ((_anchorElement$curren = (_anchorElement$curren2 = anchorElement.current) === null || _anchorElement$curren2 === void 0 ? void 0 : _anchorElement$curren2.getBoundingClientRect().top) !== null && _anchorElement$curren !== void 0 ? _anchorElement$curren : 0),
      left: (_anchorElement$curren3 = anchorElement.current) === null || _anchorElement$curren3 === void 0 ? void 0 : _anchorElement$curren3.getBoundingClientRect().right,
      config: config.stiff
    };
  });
  useLayoutEffect(function initAnchorPosition() {
    animatePositionApi.start({
      top: getAnchorPositionTop(),
      immediate: true
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function repositionOnScroll() {
    function handleScroll() {
      animatePositionApi.start({
        top: getAnchorPositionTop()
      });
    }

    navContainerElement.addEventListener('scroll', handleScroll);
    return () => navContainerElement.removeEventListener('scroll', handleScroll);
  }, [animatePositionApi, getAnchorPositionTop, navContainerElement]);

  if (!anchorElement || !anchorElement.current || !navContainerRef.current) {
    return null;
  }

  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(animated.div, {
    className: classes.root,
    style: style
  }, /*#__PURE__*/React.createElement(Flyout, {
    ref: bindFlyoutRef,
    className: classes.flyout,
    preferredFlyoutDirection: "right",
    disableResponsiveFlyoutDirection: true
  }, /*#__PURE__*/React.createElement("nav", {
    ref: navRef
  }, /*#__PURE__*/React.createElement("div", {
    role: "none",
    onKeyDown: onNavKeyDown(toggleFlyout)
  }, children)))), document.body);
});
export function useNavigationFlyout() {
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const toggleFlyout = useCallback(() => setFlyoutOpen(current => !current), []);
  const buttonRef = useRef(null);
  const flyoutRef = useRef(null);
  useOnClickOutside([buttonRef, flyoutRef], () => setFlyoutOpen(false), {
    listening: flyoutOpen,
    useEscapeKey: true
  });
  return {
    flyoutOpen,
    toggleFlyout,
    buttonRef,
    flyoutRef
  };
}